import React from "react";
import { Button, Stack, Typography } from "@mui/material";

import { CoreNetworkMemberAvatar, PagedTable, SectionCard, StatusChip, formatCurrency, formatDateTime, safeArray } from "../coreNetworkUiShared";

export default function CoreNetworkOfferPanel({
  offers = [],
  onAcceptOffer,
  onDeclineOffer,
  onCancelOffer,
  busyOfferId = null,
  canViewPayment = false,
}) {
  const columns = [
    {
      key: "member",
      label: "Member",
      render: (row) => (
        <Stack direction="row" spacing={1.25} alignItems="center">
          <CoreNetworkMemberAvatar member={row.member} size={34} />
          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.member?.displayName || "Unknown member"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.membership?.role || row.member?.memberType || "-"}
            </Typography>
          </Stack>
        </Stack>
      ),
    },
    { key: "status", label: "Status", render: (row) => <StatusChip value={row.status} /> },
    {
      key: "matchScore",
      label: "Match",
      render: (row) => {
        const score = row.match?.overallScore;
        return score != null ? `${Number(score).toFixed(1)}` : "—";
      },
    },
    {
      key: "rating",
      label: "Rating",
      render: (row) => {
        const averageRating = row.match?.scoreBreakdownJson?.rating?.averageRating;
        return averageRating != null ? `${Number(averageRating).toFixed(1)} / 5` : "New";
      },
    },
    {
      key: "availability",
      label: "Availability",
      render: (row) => row.match?.matchReasonJson?.capacity || row.member?.profileJson?.availability || "—",
    },
    ...(canViewPayment
      ? [{ key: "quotedPrice", label: "Quoted", render: (row) => formatCurrency(row.quotedPrice, row.currency || "CAD") }]
      : []),
    {
      key: "proposal",
      label: "Proposal",
      render: (row) => {
        const proposal = row.responsePayloadJson || {};
        const parts = [
          proposal.proposedFeeAmount != null ? formatCurrency(proposal.proposedFeeAmount, proposal.proposedFeeCurrency || row.currency || "CAD") : null,
          proposal.proposedCompletionDate ? `Due ${formatDateTime(proposal.proposedCompletionDate)}` : null,
        ].filter(Boolean);
        return parts.length ? parts.join(" | ") : "—";
      },
    },
    {
      key: "capabilities",
      label: "Capabilities",
      render: (row) => {
        const types = safeArray(row.match?.scoreBreakdownJson?.capabilityFit?.matchedCapabilityTypes);
        return types.length ? types.join(", ") : row.membership?.role || row.member?.memberType || "—";
      },
    },
    {
      key: "note",
      label: "Talent Note",
      render: (row) => row.responsePayloadJson?.responseNote || row.responsePayloadJson?.question || "—",
    },
    { key: "offeredAt", label: "Offered", render: (row) => formatDateTime(row.offeredAt) },
    { key: "expiresAt", label: "Expires", render: (row) => formatDateTime(row.expiresAt) },
    {
      key: "actions",
      label: "Actions",
      minWidth: 220,
      render: (row) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" disabled={!onAcceptOffer || !["pending", "sent", "viewed", "accepted", "counter_proposed"].includes(String(row.status || "").toLowerCase()) || busyOfferId === row.id} onClick={() => onAcceptOffer?.(row)}>
            Select
          </Button>
          <Button size="small" variant="outlined" color="warning" disabled={!onDeclineOffer || !["pending", "sent", "viewed", "accepted", "counter_proposed"].includes(String(row.status || "").toLowerCase()) || busyOfferId === row.id} onClick={() => onDeclineOffer?.(row)}>
            Decline
          </Button>
          <Button size="small" variant="outlined" color="error" disabled={!onCancelOffer || !["pending", "sent", "viewed"].includes(String(row.status || "").toLowerCase()) || busyOfferId === row.id} onClick={() => onCancelOffer?.(row)}>
            Cancel
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <SectionCard title="Offers" subtitle="Offer workflow and provider responses for this work request.">
      <PagedTable
        rows={safeArray(offers)}
        columns={columns}
        emptyTitle="No offers yet"
        emptyDescription="Send top-match offers once you have confidence in the provider shortlist."
      />
    </SectionCard>
  );
}
